import { useSearchParams } from 'react-router-dom';
import { useAuthStore, useDrawerStore } from '../../store';
import { useDeleteData, useFetchData, usePartnerStore } from '../../hooks';
import React, { useEffect, useState } from 'react';
import { PageableResponse, PartnerModel } from '../../types';
import { Resource } from '../../lib/enums';
import { convertDate, dateTimeFormat } from '../../lib/utils';
import { Spin } from 'antd';
import BaseTable from '../../components/BaseTable';
import InfoModal from '../../components/ui/modal/InfoModal';

const PartnerTable = ({
  api,
  moduleName,
  moduleCode,
}: {
  api: string;
  moduleName: string;
  moduleCode: string;
}) => {
  // Buyers share this table; accreditation belongs to suppliers only.
  const [searchParams] = useSearchParams();
  const isVendor = moduleName === 'Vendor';
  const drawerStore = useDrawerStore();

  const partnerStore = usePartnerStore();

  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const { user } = useAuthStore();
  const userModuleAccess = user?.moduleAccess?.find(
    (item) => item.module.code === moduleCode,
  );

  const { data, isLoading } = useFetchData<PageableResponse<PartnerModel>>(
    api,
    {
      resource: searchParams.get('resource') || Resource.FULL,
      search: searchParams.get('search') || '',
      length: parseInt(searchParams.get('length') || '999999'),
      // audit view: ?accredited=true narrows to accredited suppliers
      ...(isVendor && searchParams.get('accredited')
        ? { accredited: searchParams.get('accredited') }
        : {}),
    },
  );

  const {
    mutate,
    isPending: isPendingDelete,
    isSuccess: isSuccessDelete,
  } = useDeleteData(api);

  // Buyers share this table; accreditation columns are for suppliers only.
  // These three are the ACC_MMD "Supplier List (Report)" columns that MMD
  // could not previously show.
  const accreditationColumns = isVendor
    ? [
        {
          title: 'Material Supplied',
          dataIndex: 'materialSupplied',
          width: 160,
        },
        {
          title: 'Supplier Type',
          dataIndex: 'supplierType',
          width: 120,
        },
        {
          title: 'Accredited',
          dataIndex: 'accredited',
          width: 110,
          render: (_text: string, record: PartnerModel) =>
            record?.accredited ? 'Yes' : 'No',
        },
        {
          title: 'Date Accredited',
          dataIndex: 'dateAccredited',
          width: 140,
        },
      ]
    : [];

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 120,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 120,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      width: 120,
    },
    {
      title: 'City',
      dataIndex: 'city',
      width: 120,
      render: (_text: string, record: PartnerModel) =>
        `${record?.city?.description === undefined ? '' : record.city?.description}`,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      width: 120,
      render: (_text: string, record: PartnerModel) =>
        `${record?.country?.description === undefined ? '' : record.country?.description}`,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 120,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNo',
      width: 120,
    },
    {
      title: 'Agent Name',
      dataIndex: 'agentName',
      width: 120,
    },
    {
      title: 'Contact Finance',
      dataIndex: 'contactFinance',
      width: 120,
    },
    {
      title: 'Agent Contact',
      dataIndex: 'agentContact',
      width: 120,
    },
    {
      title: 'Contact Person',
      dataIndex: 'contactPerson',
      width: 120,
    },
    {
      title: 'Contact Shipping',
      dataIndex: 'contactShipping',
      width: 120,
    },
    {
      title: 'Line of Business',
      dataIndex: 'lineOfBusiness',
      width: 120,
      render: (_text: string, record: PartnerModel) =>
        `${record?.lineOfBusiness?.description === undefined ? '' : record.lineOfBusiness?.description}`,
    },
    ...accreditationColumns,
    {
      title: 'Created By/Date',
      dataIndex: 'createdBy',
      width: 120,
      render: (_text: string, record: any) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.createdBy}</span>
            {record.timeCreated != null && (
              <span>{convertDate(record.timeCreated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Update By/Date',
      dataIndex: 'updatedBy',
      width: 120,
      render: (_text: string, record: any) => {
        return (
          <div className="flex flex-col">
            <span className="mr-2">{record.updatedBy}</span>
            {record.timeUpdated != null && (
              <span>{convertDate(record.timeUpdated, dateTimeFormat)}</span>
            )}
          </div>
        );
      },
    },
  ];

  const handleDataDelete = (data: PartnerModel) => {
    mutate({ id: data.id });
    setSpinning(true);
  };

  const handleDataEdit = (data: PartnerModel) => {
    partnerStore.setData(data);
    drawerStore.onClose();
  };

  useEffect(() => {
    if (isSuccessDelete) setSpinning(false);
  }, [isSuccessDelete]);

  const handleInfoModalClose = () => {
    setInfoModalOpen(false);
  };

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <BaseTable
        dataSource={data?.data || []}
        isLoading={isLoading || isPendingDelete}
        handleDeleteButtonClick={handleDataDelete}
        handleEditButtonClick={handleDataEdit}
        columns={columns}
        canDelete={userModuleAccess?.delete}
        canEdit={userModuleAccess?.write}
        userRole={user?.role}
        module={moduleName}
      />
      <InfoModal
        visible={isInfoModalOpen}
        message={`${moduleName} successfully deleted`}
        onClose={handleInfoModalClose}
      />
    </>
  );
};
export default PartnerTable;
